import { useState, useEffect } from 'react'
import { Save, Settings, Mail, Phone, MapPin, Facebook, Instagram, Twitter, Building2, Clock, X, Plus, Trash2 } from 'lucide-react'
import { api } from '../../lib/api'
import AdminNavHeader from '../../components/AdminNavHeader'
import toast from 'react-hot-toast'
import { getCache, getCacheStale, setCache } from '../../lib/cache'

interface PlatformSetting {
  id: string
  key: string
  value: string
  type: 'text' | 'url' | 'email' | 'phone' | 'json'
  category: 'contact' | 'social' | 'legal' | 'general' | 'business'
  description: string | null
  is_public: boolean
  created_at: string
  updated_at: string
}

const AdminPlatformSettings = () => {
  const [settings, setSettings] = useState<Record<string, PlatformSetting[]>>({})
  const [flatSettings, setFlatSettings] = useState<PlatformSetting[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<Partial<PlatformSetting>>({})
  const [showAddModal, setShowAddModal] = useState(false)
  const [newSetting, setNewSetting] = useState({
    key: '',
    value: '',
    type: 'text' as PlatformSetting['type'],
    category: 'general' as PlatformSetting['category'],
    description: '',
    is_public: false
  })

  const categoryIcons: Record<string, any> = {
    contact: Mail,
    social: Facebook,
    business: Building2,
    legal: Settings,
    general: Settings
  }

  const categoryNames: Record<string, string> = {
    contact: 'Contacto',
    social: 'Redes Sociales',
    business: 'Negocio',
    legal: 'Legal',
    general: 'General'
  }

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    const cacheKey = 'admin_platform_settings'
    
    // Intentar cargar desde cache primero
    const cachedSettings = getCacheStale<Record<string, PlatformSetting[]>>(cacheKey)
    const cachedFlatSettings = getCacheStale<PlatformSetting[]>(`${cacheKey}_flat`)
    if (cachedSettings && cachedFlatSettings) {
      setSettings(cachedSettings)
      setFlatSettings(cachedFlatSettings)
      setLoading(false)
    }

    try {
      setLoading(true)
      const response = await api.getPlatformSettings()
      if (response.success) {
        const settingsData = response.data || {}
        const flatData = response.flat || []
        setSettings(settingsData)
        setFlatSettings(flatData)
        
        // Guardar en cache (10 minutos de TTL - configuración cambia poco)
        setCache(cacheKey, settingsData, 10 * 60 * 1000)
        setCache(`${cacheKey}_flat`, flatData, 10 * 60 * 1000)
      }
    } catch (error: any) {
      console.error('Error loading platform settings:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedSettings && cachedFlatSettings) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar configuración de la plataforma')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (setting: PlatformSetting) => {
    setEditingKey(setting.key)
    setEditForm({
      value: setting.value,
      type: setting.type,
      category: setting.category,
      description: setting.description || '',
      is_public: setting.is_public
    })
  }

  const handleSave = async (key: string) => {
    try {
      setSaving(true)
      const response = await api.updatePlatformSetting(key, editForm)
      if (response.success) {
        toast.success('Configuración actualizada exitosamente')
        setEditingKey(null)
        await loadSettings()
      }
    } catch (error: any) {
      console.error('Error saving setting:', error)
      toast.error(error.response?.data?.message || 'Error al guardar configuración')
    } finally {
      setSaving(false)
    }
  }

  const handleAdd = async () => {
    if (!newSetting.key.trim()) {
      toast.error('La clave es requerida')
      return
    }

    try {
      setSaving(true)
      const response = await api.updatePlatformSetting(newSetting.key, {
        value: newSetting.value,
        type: newSetting.type,
        category: newSetting.category,
        description: newSetting.description || null,
        is_public: newSetting.is_public
      })
      if (response.success) {
        toast.success('Configuración creada exitosamente')
        setShowAddModal(false)
        setNewSetting({
          key: '',
          value: '',
          type: 'text',
          category: 'general',
          description: '',
          is_public: false
        })
        await loadSettings()
      }
    } catch (error: any) {
      console.error('Error adding setting:', error)
      toast.error(error.response?.data?.message || 'Error al crear configuración')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (key: string) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la configuración "${key}"?`)) {
      return
    }

    try {
      setSaving(true)
      const response = await api.deletePlatformSetting(key)
      if (response.success) {
        toast.success('Configuración eliminada exitosamente')
        await loadSettings()
      }
    } catch (error: any) {
      console.error('Error deleting setting:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar configuración')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando configuración...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <AdminNavHeader
        title="Configuración de la Plataforma"
        subtitle="Gestiona información de contacto, redes sociales y datos de ColmaFácil"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Add Button */}
        <div className="mb-6 flex justify-end">
          <button
            onClick={() => setShowAddModal(true)}
            className="px-5 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl font-semibold hover:from-primary-700 hover:to-primary-800 active:scale-95 transition-all duration-200 shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Agregar Configuración
          </button>
        </div>

        {/* Settings by Category */}
        <div className="space-y-6">
          {Object.entries(settings).map(([category, categorySettings]) => {
            const Icon = categoryIcons[category] || Settings
            return (
              <div key={category} className="bg-white/90 backdrop-blur-sm rounded-3xl shadow-lg border border-white/50 p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg shadow-primary-500/20">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">{categoryNames[category] || category}</h2>
                    <p className="text-sm text-gray-500">{categorySettings.length} configuración(es)</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {categorySettings.map((setting) => (
                    <div
                      key={setting.id}
                      className="bg-gradient-to-br from-gray-50 to-gray-100/50 rounded-2xl p-5 border border-gray-200/50"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900">{setting.key}</h3>
                            {setting.is_public && (
                              <span className="px-2.5 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                                Público
                              </span>
                            )}
                            <span className="px-2.5 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                              {setting.type}
                            </span>
                          </div>
                          {setting.description && (
                            <p className="text-sm text-gray-600 mb-3">{setting.description}</p>
                          )}
                          {editingKey === setting.key ? (
                            <div className="space-y-3">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
                                <input
                                  type={setting.type === 'email' ? 'email' : setting.type === 'url' ? 'url' : 'text'}
                                  value={editForm.value || ''}
                                  onChange={(e) => setEditForm({ ...editForm, value: e.target.value })}
                                  className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                />
                              </div>
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleSave(setting.key)}
                                  disabled={saving}
                                  className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 active:scale-95 transition-all duration-200 shadow-lg shadow-primary-500/30 disabled:opacity-50 flex items-center gap-2"
                                >
                                  <Save className="w-4 h-4" />
                                  Guardar
                                </button>
                                <button
                                  onClick={() => setEditingKey(null)}
                                  className="px-5 py-2.5 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 active:scale-95 transition-all duration-200"
                                >
                                  Cancelar
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-between">
                              <p className="text-base text-gray-700 font-medium break-all">{setting.value || '(vacío)'}</p>
                              <div className="flex gap-2 ml-4">
                                <button
                                  onClick={() => handleEdit(setting)}
                                  className="px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:bg-gray-50 active:scale-95 transition-all duration-200 flex items-center gap-2"
                                >
                                  <Settings className="w-4 h-4" />
                                  Editar
                                </button>
                                <button
                                  onClick={() => handleDelete(setting.key)}
                                  disabled={saving}
                                  className="px-4 py-2 bg-white border-2 border-red-200 text-red-600 rounded-xl font-semibold hover:bg-red-50 active:scale-95 transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        {/* Add Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-6 sm:p-8 animate-slide-up">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-2xl font-bold text-gray-900">Agregar Configuración</h3>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 active:scale-90 transition-all duration-200 flex items-center justify-center"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Clave (Key) *</label>
                  <input
                    type="text"
                    value={newSetting.key}
                    onChange={(e) => setNewSetting({ ...newSetting, key: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    placeholder="contact_email"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Valor *</label>
                  <input
                    type="text"
                    value={newSetting.value}
                    onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                    placeholder="valor@ejemplo.com"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo</label>
                    <select
                      value={newSetting.type}
                      onChange={(e) => setNewSetting({ ...newSetting, type: e.target.value as any })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="text">Texto</option>
                      <option value="url">URL</option>
                      <option value="email">Email</option>
                      <option value="phone">Teléfono</option>
                      <option value="json">JSON</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Categoría</label>
                    <select
                      value={newSetting.category}
                      onChange={(e) => setNewSetting({ ...newSetting, category: e.target.value as any })}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value="contact">Contacto</option>
                      <option value="social">Redes Sociales</option>
                      <option value="business">Negocio</option>
                      <option value="legal">Legal</option>
                      <option value="general">General</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Descripción</label>
                  <input
                    type="text"
                    value={newSetting.description}
                    onChange={(e) => setNewSetting({ ...newSetting, description: e.target.value })}
                    placeholder="Descripción de esta configuración"
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={newSetting.is_public}
                    onChange={(e) => setNewSetting({ ...newSetting, is_public: e.target.checked })}
                    className="w-5 h-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded cursor-pointer"
                  />
                  <label className="text-sm font-medium text-gray-700">Configuración pública (accesible sin autenticación)</label>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={handleAdd}
                  disabled={saving || !newSetting.key.trim()}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-2xl font-semibold hover:from-primary-700 hover:to-primary-800 active:scale-95 transition-all duration-200 shadow-lg shadow-primary-500/30 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  <Save className="w-5 h-5" />
                  Crear
                </button>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-6 py-3 bg-white border-2 border-gray-200 text-gray-700 rounded-2xl font-semibold hover:bg-gray-50 active:scale-95 transition-all duration-200"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminPlatformSettings

